import { type FC, useState } from "react";
import { MessageCircleQuestion } from "lucide-react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { uploadDocumentWithAuth } from "@/shared/utils/uploadSupportingDocument";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";
import { sentryUtils } from "@/shared/utils/sentry";

import {
  OtherLoanForm,
  EMPTY_OTHER_LOAN_FORM,
  validateOtherLoanFields,
  otherLoanToItemFields,
  type OtherLoanFormValues,
  type OtherLoanFieldErrors,
} from "./OtherLoanForm";

export const AddOtherLoan: FC<FlowCustomComponentProps> = ({ answers, goToScreen }) => {
  const { person, user } = useAppContext();
  const estateId = person?.estate_id;
  const { addItem, isAddingItem } = useEstateItems(estateId);

  const { form, fieldErrors, setFieldErrors, updateForm } = useFormWithErrors<
    OtherLoanFormValues,
    OtherLoanFieldErrors
  >(EMPTY_OTHER_LOAN_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const errors = validateOtherLoanFields(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    if (!estateId) return;

    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      let statementDocId: number | undefined;

      if (form.statementFile) {
        statementDocId = await uploadDocumentWithAuth(
          form.statementFile,
          estateId,
          "loan_statement",
        );
      }

      const fields = otherLoanToItemFields(form);

      await addItem({
        estate_id: estateId,
        category: "Unsecured Debts",
        classification: "debt",
        item_name: fields.item_name,
        item_type: fields.item_type,
        balance: fields.balance,
        metadata: fields.metadata,
        source_document_id: statementDocId,
      });

      goToScreen("review_other_loans", {
        ...answers,
        add_other_loan: "done",
        review_other_loans: "found",
      });
    } catch (err) {
      setError("Failed to add loan. Please try again.");
      sentryUtils.captureError(err, "AddOtherLoan submission failed", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <AddItemLayout
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        error={error}
        disabled={isAddingItem}
      >
        <OtherLoanForm form={form} onChange={updateForm} fieldErrors={fieldErrors} />
      </AddItemLayout>

      <div className="mx-auto mt-8 flex w-full max-w-[600px] items-start gap-3 rounded-lg border border-primary bg-background p-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-primary bg-white">
          <MessageCircleQuestion className="h-4 w-4 text-secondary" />
        </div>
        <div>
          <p className="font-medium">Why do we need this information?</p>
          <p className="text-secondary">
            Our team will review this information and let you know if benefits are available.
          </p>
        </div>
      </div>
    </>
  );
};
